import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import SecureVideoPlayer from '../../components/common/SecureVideoPlayer';
import {
  createPatientResource,
  deletePatientResource,
  getPatientResources,
  getPatientResourceFileUrl,
  updatePatientResource,
} from '../../services/userService';

function LazyThumbImage({ src, alt, className }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '250px' }
    );

    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {isVisible && !errored ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          style={loaded ? undefined : { opacity: 0 }}
        />
      ) : null}

      {!loaded && (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <Icon icon="fluent:image-20-regular" width="20" className="text-gray-400" />
        </div>
      )}
    </div>
  );
}

function PatientResources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadQueueIndex, setUploadQueueIndex] = useState(0);
  const [uploadQueueTotal, setUploadQueueTotal] = useState(0);

  const [viewMode, setViewMode] = useState('list');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [sortBy, setSortBy] = useState('newest');

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [selected, setSelected] = useState(null);

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  const previewRequestIdRef = useRef(0);
  const fileUrlCacheRef = useRef(new Map());

  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [resourceType, setResourceType] = useState('document');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [files, setFiles] = useState([]);
  const [tagsInput, setTagsInput] = useState('');

  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editTagsInput, setEditTagsInput] = useState('');
  const [editYoutubeUrl, setEditYoutubeUrl] = useState('');

  const parseTags = (raw) => {
    const str = String(raw || '').trim();
    if (!str) return [];
    return str
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchResources = async (opts = {}) => {
    const nextSearch = opts.search !== undefined ? opts.search : debouncedSearch;
    const nextType = opts.type !== undefined ? opts.type : typeFilter;
    const nextPage = opts.page !== undefined ? opts.page : 1;

    setLoading(true);
    try {
      const params = {
        page: nextPage,
        limit: 25,
      };
      if (nextSearch) params.search = nextSearch;
      if (nextType) params.type = nextType;

      const resp = await getPatientResources(params);
      const list = resp?.data?.resources || resp?.data?.data?.resources || resp?.resources || [];
      setResources(Array.isArray(list) ? list : []);
      setPage(nextPage);
      const totalPages = resp?.data?.pagination?.totalPages || resp?.data?.data?.pagination?.totalPages;
      if (totalPages) {
        setHasMore(nextPage < totalPages);
      } else {
        setHasMore(list.length >= 25);
      }
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loading || !hasMore) return;

    const next = page + 1;
    setLoading(true);
    try {
      const params = {
        page: next,
        limit: 25,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter) params.type = typeFilter;

      const resp = await getPatientResources(params);
      const list = resp?.data?.resources || resp?.data?.data?.resources || resp?.resources || [];
      setResources((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        const merged = [...prev];
        (Array.isArray(list) ? list : []).forEach((r) => {
          if (!seen.has(r.id)) merged.push(r);
        });
        return merged;
      });

      setPage(next);
      const totalPages = resp?.data?.pagination?.totalPages || resp?.data?.data?.pagination?.totalPages;
      if (totalPages) {
        setHasMore(next < totalPages);
      } else {
        setHasMore((Array.isArray(list) ? list : []).length >= 25);
      }
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to load more resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources({ page: 1 });
  }, []);

  useEffect(() => {
    setPage(1);
    fetchResources({ page: 1, search: debouncedSearch, type: typeFilter });
  }, [debouncedSearch, typeFilter]);

  useEffect(() => {
    if (selected && !resources.find((r) => r.id === selected.id)) {
      setSelected(null);
      setIsViewerOpen(false);
    }
  }, [resources, selected]);

  useEffect(() => {
    return () => {};
  }, []);

  const filteredResources = useMemo(() => {
    const sorted = [...resources];
    if (sortBy === 'oldest') {
      sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortBy === 'title_asc') {
      sorted.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    } else {
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return sorted;
  }, [resources, sortBy]);

  const resetCreateForm = () => {
    setTitle('');
    setResourceType('document');
    setYoutubeUrl('');
    setFiles([]);
    setTagsInput('');
    setUploadPercent(0);
    setIsUploading(false);
    setIsProcessing(false);
    setUploadQueueIndex(0);
    setUploadQueueTotal(0);
  };

  const fileNameToTitle = (fileName) => {
    const raw = String(fileName || '').trim();
    if (!raw) return 'Untitled';
    const withoutPath = raw.split('/').pop();
    const dot = withoutPath.lastIndexOf('.');
    const base = dot > 0 ? withoutPath.slice(0, dot) : withoutPath;
    return String(base || 'Untitled');
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (resourceType === 'youtube' && !youtubeUrl.trim()) {
      toast.error('YouTube URL is required');
      return;
    }

    if (resourceType !== 'youtube' && (!files || files.length === 0)) {
      toast.error('File is required');
      return;
    }

    if (resourceType === 'youtube' && !title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (resourceType !== 'youtube' && resourceType !== 'image' && !title.trim()) {
      toast.error('Title is required');
      return;
    }

    const uploadList = resourceType === 'youtube' ? [] : (Array.isArray(files) ? files : []).filter(Boolean);
    const totalUploads = resourceType === 'youtube' ? 0 : uploadList.length;

    setSaving(true);
    setIsUploading(resourceType !== 'youtube');
    setIsProcessing(false);
    setUploadPercent(0);
    setUploadQueueIndex(0);
    setUploadQueueTotal(totalUploads);
    try {
      if (resourceType === 'youtube') {
        const formData = new FormData();
        formData.append('title', title.trim());
        formData.append('resource_type', resourceType);
        formData.append('tags', JSON.stringify(parseTags(tagsInput)));
        formData.append('youtube_url', youtubeUrl.trim());

        const resp = await createPatientResource(formData);
        if (resp?.success === false) {
          toast.error(resp?.data?.error_msg || 'Failed to create resource');
          return;
        }
        toast.success(resp?.data?.message || 'Resource added');
      } else if (resourceType === 'image' && uploadList.length > 0) {
        let createdCount = 0;
        for (let i = 0; i < uploadList.length; i += 1) {
          const currentFile = uploadList[i];
          setUploadQueueIndex(i + 1);
          setUploadPercent(0);
          setIsUploading(true);
          setIsProcessing(false);

          const imageTitle = title.trim() ? title.trim() : fileNameToTitle(currentFile?.name);
          const formData = new FormData();
          formData.append('title', imageTitle);
          formData.append('resource_type', resourceType);
          formData.append('tags', JSON.stringify(parseTags(tagsInput)));
          formData.append('file', currentFile);

          const resp = await createPatientResource(formData, {
            onUploadProgress: (progressEvent) => {
              const total = progressEvent?.total || 0;
              const loaded = progressEvent?.loaded || 0;
              if (!total) return;
              const pct = Math.min(100, Math.max(0, Math.round((loaded * 100) / total)));
              setUploadPercent(pct);
              if (pct >= 100) {
                setIsUploading(false);
                setIsProcessing(true);
              }
            },
          });

          if (resp?.success === false) {
            toast.error(resp?.data?.error_msg || `Failed to upload ${currentFile?.name || 'file'}`);
            return;
          }
          createdCount += 1;
        }
        toast.success(createdCount === 1 ? '1 image uploaded' : `${createdCount} images uploaded`);
      } else {
        const currentFile = uploadList[0];
        const formData = new FormData();
        formData.append('title', title.trim());
        formData.append('resource_type', resourceType);
        formData.append('tags', JSON.stringify(parseTags(tagsInput)));
        formData.append('file', currentFile);

        const resp = await createPatientResource(formData, {
          onUploadProgress: (progressEvent) => {
            const total = progressEvent?.total || 0;
            const loaded = progressEvent?.loaded || 0;
            if (!total) return;
            const pct = Math.min(100, Math.max(0, Math.round((loaded * 100) / total)));
            setUploadPercent(pct);
            if (pct >= 100) {
              setIsUploading(false);
              setIsProcessing(true);
            }
          },
        });
        if (resp?.success === false) {
          toast.error(resp?.data?.error_msg || 'Failed to create resource');
          return;
        }
        toast.success(resp?.data?.message || 'Resource added');
      }

      resetCreateForm();
      setShowCreate(false);
      fetchResources();
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to create resource');
    } finally {
      setSaving(false);
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setEditTitle(r.title || '');
    setEditTagsInput(Array.isArray(r.tags) ? r.tags.join(', ') : '');
    setEditYoutubeUrl(r.youtube_url || '');
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditTitle('');
    setEditTagsInput('');
    setEditYoutubeUrl('');
  };

  const saveEdit = async (r) => {
    if (!editTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    if (r.resource_type === 'youtube' && !editYoutubeUrl.trim()) {
      toast.error('YouTube URL is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: editTitle.trim(),
        tags: JSON.stringify(parseTags(editTagsInput)),
      };
      if (r.resource_type === 'youtube') {
        payload.youtube_url = editYoutubeUrl.trim();
      }

      const resp = await updatePatientResource(r.id, payload);
      if (resp?.success === false) {
        toast.error(resp?.data?.error_msg || 'Failed to update resource');
        return;
      }

      toast.success(resp?.data?.message || 'Updated');
      cancelEdit();
      fetchResources();
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to update resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    const ok = window.confirm('Delete this resource?');
    if (!ok) return;

    setSaving(true);
    try {
      const resp = await deletePatientResource(r.id);
      if (resp?.success === false) {
        toast.error(resp?.data?.error_msg || 'Failed to delete resource');
        return;
      }
      toast.success(resp?.data?.message || 'Deleted');
      if (selected?.id === r.id) {
        setSelected(null);
        setIsViewerOpen(false);
      }
      fetchResources();
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to delete resource');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const getStableFileUrl = (resourceId) => {
    if (!resourceId) return '';
    const cached = fileUrlCacheRef.current.get(resourceId);
    if (cached) return cached;
    const url = getPatientResourceFileUrl(resourceId);
    fileUrlCacheRef.current.set(resourceId, url);
    return url;
  };

  const openViewer = async (r) => {
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;

    setSelected(r);
    setIsViewerOpen(true);

    setPreviewUrl(null);
    setPreviewType(null);
    setPreviewLoading(true);

    if (r.resource_type === 'youtube') {
      setPreviewType('youtube');
      setPreviewLoading(false);
      return;
    }

    const streamUrl = getStableFileUrl(r.id);

    if (r.resource_type === 'video') {
      setPreviewType('video');
      setPreviewUrl(streamUrl);
      setPreviewLoading(false);
      return;
    }

    if (r.resource_type === 'image') {
      setPreviewType('image');
      setPreviewUrl(streamUrl);
      setPreviewLoading(false);
      return;
    }

    // document
    setPreviewType('pdf');
    setPreviewUrl(streamUrl);
    setPreviewLoading(false);
  };

  const markPreviewLoaded = (requestId) => {
    if (previewRequestIdRef.current !== requestId) return;
    setPreviewLoading(false);
  };

  const getPreview = (r) => {
    if (!r) return null;

    if (r.resource_type === 'youtube') {
      const url = String(r.youtube_url || '');
      const match = url.match(/[?&]v=([^&]+)/) || url.match(/youtu\.be\/([^?]+)/);
      const id = match?.[1];
      if (!id) {
        return {
          type: 'link',
          url,
        };
      }
      return {
        type: 'youtube',
        url: `https://www.youtube.com/embed/${id}`,
      };
    }

    const fileUrl = r.file_url;
    if (!fileUrl) return null;
    if (r.resource_type === 'video') return { type: 'video', url: previewUrl || fileUrl };
    if (r.resource_type === 'image') return { type: 'image', url: previewUrl || fileUrl };

    const lower = String(fileUrl).toLowerCase();
    if (lower.includes('.pdf')) return { type: 'pdf', url: previewUrl || fileUrl };
    return { type: 'download', url: fileUrl };
  };

  const selectedPreview = useMemo(() => getPreview(selected), [selected, previewUrl]);

  const hasActiveFilters = Boolean(debouncedSearch || typeFilter);

  const typeLabel = (t) => {
    if (t === 'youtube') return 'YouTube';
    if (t === 'video') return 'Video';
    if (t === 'image') return 'Image';
    return 'Document';
  };

  const getThumb = (r) => {
    if (!r) return { kind: 'icon', icon: 'fluent:document-20-regular' };
    if (r.resource_type === 'image') {
      return { kind: 'image', url: getStableFileUrl(r.id) };
    }
    if (r.resource_type === 'video') {
      return { kind: 'icon', icon: 'fluent:video-20-regular' };
    }
    if (r.resource_type === 'youtube') {
      return { kind: 'icon', icon: 'logos:youtube-icon' };
    }
    return { kind: 'icon', icon: 'fluent:document-20-regular' };
  };

  return (
    <div className="px-4 py-4 md:px-8">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon icon="fluent:document-heart-24-regular" width="20" className="text-primary" />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-800">Patient Resources</h1>
            <p className="text-xs md:text-sm text-gray-600">Private library for your patients. View documents and videos inside the platform.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchResources()}
            disabled={loading}
            className="px-3 py-2 border rounded-lg text-sm disabled:opacity-60"
            title="Refresh"
          >
            <div className="flex items-center gap-2">
              <Icon icon="fluent:arrow-clockwise-20-regular" width="18" />
              <span>Refresh</span>
            </div>
          </button>

          <button
            onClick={() => setShowCreate(true)}
            className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium"
          >
            <div className="flex items-center gap-2">
              <Icon icon="fluent:add-20-regular" width="18" />
              <span>New Resource</span>
            </div>
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <div className="sticky top-0 z-20 -mx-4 md:-mx-8 px-4 md:px-8 py-3 bg-white/90 backdrop-blur border-b border-gray-100">
          <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="18" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or tags"
              className="pl-10 pr-3 py-2 border rounded-lg text-sm w-72 max-w-full bg-white"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="py-2 px-3 border rounded-lg text-sm bg-white"
          >
            <option value="">All types</option>
            <option value="document">Documents</option>
            <option value="video">Videos</option>
            <option value="image">Images</option>
            <option value="youtube">YouTube</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="py-2 px-3 border rounded-lg text-sm bg-white"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A-Z</option>
          </select>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={
                viewMode === 'list'
                  ? 'px-3 py-2 rounded-lg text-sm font-medium bg-primary text-white'
                  : 'px-3 py-2 rounded-lg text-sm border bg-white text-gray-700'
              }
              title="List view"
            >
              <div className="flex items-center gap-2">
                <Icon icon="fluent:list-20-regular" width="18" />
                <span>List</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={
                viewMode === 'grid'
                  ? 'px-3 py-2 rounded-lg text-sm font-medium bg-primary text-white'
                  : 'px-3 py-2 rounded-lg text-sm border bg-white text-gray-700'
              }
              title="Grid view"
            >
              <div className="flex items-center gap-2">
                <Icon icon="fluent:grid-20-regular" width="18" />
                <span>Grid</span>
              </div>
            </button>
          </div>

          </div>
        </div>

        
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className={isViewerOpen ? 'lg:col-span-4' : 'lg:col-span-12'}>
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-800">Library</div>
                <div className="text-xs text-gray-500">{filteredResources.length} item(s)</div>
              </div>
              <div className="text-xs text-gray-500">{loading ? 'Loading...' : ''}</div>
            </div>

            {loading && filteredResources.length === 0 ? (
              <>
                {viewMode === 'grid' ? (
                  <div
                    className="p-4 max-h-[70vh] min-h-[70vh] overflow-auto overscroll-contain bg-white"
                    style={{ WebkitOverflowScrolling: 'touch', transform: 'translateZ(0)' }}
                  >
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-2">
                      {Array.from({ length: 12 }).map((_, idx) => (
                        <div key={idx} className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                          <div className="w-full aspect-[1/1] bg-gray-100 animate-pulse" />
                          <div className="p-2 space-y-1">
                            <div className="h-3 bg-gray-100 rounded animate-pulse" />
                            <div className="h-3 w-2/3 bg-gray-100 rounded animate-pulse" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    className="divide-y max-h-[70vh] min-h-[70vh] overflow-auto overscroll-contain bg-white"
                    style={{ WebkitOverflowScrolling: 'touch', transform: 'translateZ(0)' }}
                  >
                    {Array.from({ length: 10 }).map((_, idx) => (
                      <div key={idx} className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 animate-pulse" />
                          <div className="flex-1 min-w-0">
                            <div className="h-4 bg-gray-100 rounded animate-pulse" />
                            <div className="h-3 w-1/2 bg-gray-100 rounded mt-2 animate-pulse" />
                          </div>
                          <div className="w-16 h-9 bg-gray-100 rounded-lg animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : filteredResources.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-800 font-medium">{hasActiveFilters ? 'No matches found' : 'No resources yet'}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {hasActiveFilters
                    ? 'Try adjusting your search or filters.'
                    : 'Add your first resource to build your library.'}
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              <div
                className="p-4 max-h-[70vh] min-h-[70vh] overflow-auto overscroll-contain bg-white"
                style={{ WebkitOverflowScrolling: 'touch', transform: 'translateZ(0)' }}
              >
                {loading && (
                  <div className="sticky top-0 z-10 -mx-4 px-4 py-2 bg-white/90 backdrop-blur border-b border-gray-100">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Icon icon="fluent:spinner-ios-20-regular" width="16" className="animate-spin" />
                      <span>Loading…</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-2">
                  {filteredResources.map((r) => {
                    const active = selected?.id === r.id;
                    const thumb = getThumb(r);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => openViewer(r)}
                        className={
                          active
                            ? 'text-left rounded-2xl border border-[#7083FF] bg-[#7083FF]/5 shadow-sm overflow-hidden'
                            : 'text-left rounded-2xl border border-gray-100 bg-white hover:shadow-sm overflow-hidden'
                        }
                      >
                        <div className="w-full aspect-[1/1] bg-gray-100 flex items-center justify-center overflow-hidden">
                          {thumb.kind === 'image' ? (
                            <LazyThumbImage
                              src={thumb.url}
                              alt={r.title}
                              className="w-full h-full"
                            />
                          ) : (
                            <Icon icon={thumb.icon} width="22" className="text-gray-500" />
                          )}
                        </div>
                        <div className="p-2">
                          <div className="font-semibold text-xs text-gray-900 line-clamp-1" title={r.title}>{r.title}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {typeLabel(r.resource_type)}
                            {r.createdAt ? ` · ${formatDate(r.createdAt)}` : ''}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {hasMore && (
                  <div className="p-4">
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-60"
                    >
                      {loading ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="divide-y max-h-[70vh] min-h-[70vh] overflow-auto overscroll-contain bg-white"
                style={{ WebkitOverflowScrolling: 'touch', transform: 'translateZ(0)' }}
              >
                {loading && (
                  <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-100 px-3 py-2">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Icon icon="fluent:spinner-ios-20-regular" width="16" className="animate-spin" />
                      <span>Loading…</span>
                    </div>
                  </div>
                )}
                {filteredResources.map((r) => {
                  const active = selected?.id === r.id;
                  const thumb = getThumb(r);
                  return (
                    <button
                      key={r.id}
                      onClick={() => openViewer(r)}
                      className={
                        active
                          ? 'w-full text-left p-3 bg-primary/5 ring-1 ring-primary/30'
                          : 'w-full text-left p-3 bg-white hover:bg-gray-50'
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {thumb.kind === 'image' ? (
                            <LazyThumbImage
                              src={thumb.url}
                              alt={r.title}
                              className="w-full h-full"
                            />
                          ) : (
                            <Icon icon={thumb.icon} width="20" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-medium text-gray-800 truncate">{r.title}</div>
                              <div className="text-xs text-gray-500">
                                {typeLabel(r.resource_type)}
                                {r.createdAt ? ` · ${formatDate(r.createdAt)}` : ''}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openViewer(r);
                              }}
                              className="px-3 py-2 border rounded-lg text-sm flex-shrink-0"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}

                {hasMore && (
                  <div className="p-4">
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-60"
                    >
                      {loading ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isViewerOpen && (
        <div className="lg:col-span-8 lg:sticky lg:top-6 self-start">
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="p-4 border-b flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-gray-800 truncate">{selected?.title || 'Preview'}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {selected ? typeLabel(selected.resource_type) : ''}
                  {selected?.createdAt ? ` · ${formatDate(selected.createdAt)}` : ''}
                </div>
              </div>

              {selected && editId !== selected.id && (
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {selected.resource_type !== 'youtube' && (
                    <a
                      href={getPatientResourceFileUrl(selected.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-2 border rounded-lg text-sm"
                      title="Open in new tab"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open
                    </a>
                  )}
                  {selected.resource_type !== 'youtube' && selected.resource_type !== 'video' && (
                    <a
                      href={getPatientResourceFileUrl(selected.id)}
                      download
                      className="px-3 py-2 border rounded-lg text-sm"
                      title="Download"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Download
                    </a>
                  )}
                  <button
                    onClick={() => startEdit(selected)}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(selected)}
                    disabled={saving}
                    className="px-3 py-2 border rounded-lg text-sm text-red-600 disabled:opacity-60"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>

            <div className="p-4">
              {!selected ? (
                <div className="text-sm text-gray-600">Select an item to preview.</div>
              ) : editId === selected.id ? (
                <div className="space-y-3 max-w-2xl">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  {selected.resource_type === 'youtube' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">YouTube URL</label>
                      <input
                        value={editYoutubeUrl}
                        onChange={(e) => setEditYoutubeUrl(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tags (comma separated)</label>
                    <input
                      value={editTagsInput}
                      onChange={(e) => setEditTagsInput(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(selected)}
                      disabled={saving}
                      className="px-3 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-60"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-2 border rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {previewLoading && (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-8 h-8 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                    </div>
                  )}
                  {selectedPreview?.type === 'video' && (
                    <div className="w-full rounded-lg bg-black max-h-[70vh] overflow-hidden">
                      <SecureVideoPlayer
                        key={`video:${selected?.id || ''}:${selectedPreview.url}`}
                        videoUrl={selectedPreview.url}
                        autoplay={false}
                        controls={true}
                        fluid={true}
                        responsive={true}
                        className="w-full"
                      />
                    </div>
                  )}

                  {selectedPreview?.type === 'image' && (
                    <div className="w-full flex justify-center">
                      <img
                        key={`image:${selected?.id || ''}:${selectedPreview.url}`}
                        src={selectedPreview.url}
                        alt={selected?.title || 'Image'}
                        className="max-h-[70vh] object-contain"
                        loading="lazy"
                        onLoad={() => setPreviewLoading(false)}
                        onError={() => setPreviewLoading(false)}
                      />
                    </div>
                  )}

                  {selectedPreview?.type === 'pdf' && (
                    <iframe
                      key={`pdf:${selected?.id || ''}:${selectedPreview.url}`}
                      title="Document Preview"
                      src={selectedPreview.url}
                      className="w-full h-[70vh] rounded-lg border"
                      onLoad={() => setPreviewLoading(false)}
                    />
                  )}

                  {selectedPreview?.type === 'youtube' && (
                    <iframe
                      key={selectedPreview.url}
                      title="YouTube Preview"
                      src={selectedPreview.url}
                      className="w-full h-[70vh] rounded-lg border"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}

                  {selectedPreview?.type === 'link' && (
                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-700">This YouTube link can’t be embedded. Open it:</div>
                      <a
                        href={selectedPreview.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary underline break-all"
                      >
                        {selectedPreview.url}
                      </a>
                    </div>
                  )}

                  {selectedPreview?.type === 'download' && (
                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-700">Preview isn’t available for this file type. Download:</div>
                      <a
                        href={selectedPreview.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary underline break-all"
                      >
                        {selectedPreview.url}
                      </a>
                    </div>
                  )}

                  {!selectedPreview && (
                    <div className="text-sm text-gray-600">No preview available.</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setShowCreate(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-xl border shadow-xl overflow-hidden">
            <div className="p-4 border-b flex items-start justify-between">
              <div>
                <div className="font-semibold text-gray-800">New Resource</div>
                <div className="text-xs text-gray-500">Add a document, video, or YouTube link</div>
              </div>
              <button
                onClick={() => !saving && setShowCreate(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Close"
              >
                <Icon icon="fluent:dismiss-20-regular" width="18" />
              </button>
            </div>

            <div className="p-4">
              <form onSubmit={handleCreate} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. Knee surgery rehab protocol"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select
                    value={resourceType}
                    onChange={(e) => setResourceType(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="document">Document</option>
                    <option value="video">Video</option>
                    <option value="image">Image</option>
                    <option value="youtube">YouTube link</option>
                  </select>
                </div>

                {resourceType === 'youtube' ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">YouTube URL</label>
                    <input
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">File</label>
                    <input
                      type="file"
                      multiple={resourceType === 'image'}
                      accept={
                        resourceType === 'video'
                          ? 'video/*'
                          : resourceType === 'image'
                            ? 'image/*'
                            : '.pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain'
                      }
                      onChange={(e) => setFiles(Array.from(e.target.files || []))}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Max video size: 350MB</p>
                    {resourceType === 'image' && files.length > 0 && (
                      <p className="text-[11px] text-gray-500 mt-1">Selected: {files.length} image{files.length === 1 ? '' : 's'}</p>
                    )}

                    {resourceType === 'image' && files.length > 1 && (
                      <div className="mt-2 border rounded-lg bg-gray-50 p-2 max-h-24 overflow-auto">
                        {files.slice(0, 10).map((f) => (
                          <div key={f.name} className="text-[11px] text-gray-600 truncate" title={f.name}>
                            {f.name}
                          </div>
                        ))}
                        {files.length > 10 && (
                          <div className="text-[11px] text-gray-500 mt-1">+{files.length - 10} more</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tags (comma separated)</label>
                  <input
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="knee, surgery, rehab"
                  />
                </div>

                {saving && resourceType !== 'youtube' && (
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1">
                      <span>
                        {isUploading ? 'Uploading...' : isProcessing ? 'Processing...' : 'Saving...'}
                        {resourceType === 'image' && uploadQueueTotal > 1 ? ` (${uploadQueueIndex}/${uploadQueueTotal})` : ''}
                      </span>
                      <span>{isUploading ? `${uploadPercent}%` : ''}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-2 bg-primary transition-all"
                        style={{ width: `${isUploading ? uploadPercent : 100}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => !saving && setShowCreate(false)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-primary text-white rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-60"
                  >
                    {saving
                      ? isUploading
                        ? `Uploading ${uploadPercent}%...`
                        : isProcessing
                          ? 'Processing...'
                          : 'Saving...'
                      : 'Add Resource'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientResources;
